// ── The evidence bulk bar — a SIBLING of the people bar, not a lane inside it ───────────────────
//
// ⚠ WHY A SIBLING. BulkPeopleBar is about people: every action picks a person for a lane. Provisioning
//   has no person to pick, and bolting it into that bar would make a people surface carry a repository
//   action. Same selection, same visible-rows invariant, same report-after honesty — its own bar.
//
// ⚠ VISIBLE ROWS ONLY. `selected` is the page's visible-and-selected set; a control a filter is hiding
//   is never provisioned.
//
// ⚠ THE REPORT IS THE RUN RECORD. Provisioning is a queued job the tenant can leave and return to, so
//   the result is not a transient toast: starting a run links to its durable record. What the server
//   refused UP FRONT (controls not in this workspace) is named here, immediately.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useEvidenceLibraryStatus, useStartProvisioningRun } from '@/api/hooks/useEvidenceProvisioning';
import type { StartProvisioningResult } from '@/api/types/evidence-provisioning.types';
import { runRecordPath } from './EvidenceLocationPanel';
import { PROVISIONING_ACTION, REJECTED_LABEL, apiErrorMessage } from './provisioning-copy';

const BTN = 'rounded-[7px] px-[11px] py-[6px] text-[12px] transition-colors disabled:opacity-40';

export function BulkEvidenceBar({ selected, rows, onClear }: {
  /** References of the VISIBLE, selected rows — never a filter-hidden one. */
  selected: string[];
  /** The visible rows, so a refusal is named by the code the user sees. */
  rows: { control_reference: string; display_code: string }[];
  onClear: () => void;
}) {
  const start = useStartProvisioningRun();
  const { data: library } = useEvidenceLibraryStatus();
  // ⚠ Provisioning needs a connected library. Offering the action without one would be a button the
  //   server refuses — so the bar points at the step that has to come first.
  const connected = library?.state === 'connected';
  const [started, setStarted] = useState<StartProvisioningResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const n = selected.length;

  const provision = () => {
    setError(null);
    setStarted(null);
    start.mutate({ controlReferences: selected, scope: 'bulk' }, {
      onSuccess: (r) => { setStarted(r); onClear(); },
      onError: (err) => setError(apiErrorMessage(err, 'The provisioning run could not be started.')),
    });
  };

  return (
    <div className="mb-[10px] flex flex-col gap-[8px]">
      <div role="region" aria-label="Evidence actions"
        className={cn('flex flex-wrap items-center gap-[10px] rounded-[9px] border px-[14px] py-[10px]',
          n > 0 ? 'border-accent-cyan/35 bg-accent-cyan/8' : 'border-border-subtle bg-bg-surface-2')}>
        <span className={cn('text-[12.5px] font-medium', n > 0 ? 'text-text-primary' : 'text-text-faint')}>
          Evidence
        </span>
        <button type="button" disabled={n === 0 || start.isPending || !connected} onClick={provision}
          className={cn(BTN, 'border border-accent-cyan/50 bg-bg-surface text-text-primary')}>
          {PROVISIONING_ACTION}{n > 0 ? ` (${n})` : ''}
        </button>
        {library && !connected ? (
          <p className="w-full text-[11.5px] text-text-secondary" data-testid="evidence-bar-connect-first">
            {library.state === 'connecting'
              ? `Connecting ${library.library_name ?? 'your Evidence library'} — provisioning is available once it is connected.`
              : <>No evidence repository connected yet.{' '}
                  <Link to="/compliance/workspace/evidence/library" className="text-accent-cyan hover:underline">Connect your Evidence library</Link>
                  {' '}to provision folders.</>}
          </p>
        ) : (
          <p className="w-full text-[11.5px] text-text-faint">
            {/* ⓘ Box GA S3 (item 10) — neutral: for Box the name is a folder, not a "library". */}
            Creates each selected control&apos;s folder in {library?.library_name ? library.library_name : 'your evidence repository'}.
            Controls that already have one are left as they are. A folder is where evidence goes — it is not evidence.
          </p>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-[9px] border border-cert-warn-border bg-bg-surface-2 p-[12px] text-[12.5px] text-cert-warn-fg">
          {error}
        </div>
      )}

      {started && (
        <div className="rounded-[9px] border border-border-subtle bg-bg-surface-2 p-[14px]">
          <p className="text-[13px] text-text-primary">
            Provisioning started for {started.accepted} {started.accepted === 1 ? 'control' : 'controls'}
            {started.rejected.length > 0 && <> · Not included {started.rejected.length}</>}
          </p>
          {started.rejected.length > 0 && (
            // ⚠ NAMED, NOT COUNTED — these are the controls the user asked for and did not get.
            <ul className="mt-[6px] space-y-[3px]">
              {started.rejected.map((r) => (
                <li key={r.reference} className="text-[12px] text-cert-warn-fg">
                  {rows.find((x) => x.control_reference === r.reference)?.display_code ?? r.reference} — {REJECTED_LABEL[r.reason]}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-[8px] text-[12px] text-text-secondary">
            It runs in the background — you can leave this page.{' '}
            <Link to={runRecordPath(started.run_id)} className="text-accent-cyan hover:underline">View the run</Link>
          </p>
          <button type="button" onClick={() => setStarted(null)} className={cn(BTN, 'mt-[6px] text-text-secondary')}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
